"use client";
import { useState, useEffect, useCallback } from "react";
import Image from "next/image";
import { Link } from "@/navigation";
import { useModalInquiryContext } from "@/context/ModalInquiryContext";
import { CloudArrowUp, LockSimple } from "@phosphor-icons/react";
import { useTranslations } from "next-intl";

const CAROUSEL_IMAGES = [
	"/image/new-version/machining/cnc-milling/banner/online-custom-cnc-milling-services-01.png",
	"/image/new-version/machining/cnc-milling/banner/online-custom-cnc-milling-services-02.png",
	"/image/new-version/machining/cnc-milling/banner/online-custom-cnc-milling-services-03.png",
];

const CAROUSEL_IMAGE_HEIGHT = 150;
const CAROUSEL_IMAGE_SCALES = [1, 1, 2];

// ─── 内部子组件 ───

const UploadIcon = ({ className }: { className?: string }) => (
	<svg viewBox="12 14 40 32" className={className}>
		<path
			fill="#0F7E03"
			d="M15,44V15c0-0.55,0.45-1,1-1h7.73c0.25,0,0.5,0.1,0.68,0.27l4.26,3.99c0.19,0.17,0.43,0.27,0.68,0.27H48c0.55,0,1,0.45,1,1V44c0,0.55-0.45,1-1,1H16C15.45,45,15,44.55,15,44z"
		/>
		<path
			fill="#65A525"
			d="M12.18,27.16c-0.1-0.61,0.37-1.16,0.99-1.16h37.66c0.61,0,1.08,0.55,0.99,1.16l-2.68,17c-0.08,0.49-0.5,0.84-0.99,0.84H15.85c-0.49,0-0.91-0.36-0.99-0.84L12.18,27.16z"
		/>
	</svg>
);

const CheckIcon = () => (
	<svg
		className="h-5 w-5 flex-shrink-0 text-themePrimary400"
		viewBox="0 0 20 20"
		fill="none"
		stroke="currentColor"
		strokeWidth="2.5"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="M4 10 Q 7 14 9 12 Q 13 8 16 5" />
	</svg>
);

// ─── 主组件 ───

const Banner = () => {
	const tMilling = useTranslations("cncMillingBanner");
	const t = useTranslations("serviceBanner");
	const { openModalInquiry } = useModalInquiryContext();
	const [carouselIndex, setCarouselIndex] = useState(0);

	const title = tMilling("mainTitle");
	const features = [tMilling("feature1"), tMilling("feature2")];
	const primaryButtonText = tMilling("primaryButtonText");
	const secondaryButtonText = tMilling("secondaryButtonText");
	const uploadTitle = t("uploadTitle");
	const uploadSubtitle = t("uploadSubtitle");
	const uploadFormats = t("uploadFormats");
	const uploadSecure = t("uploadSecure");
	const fileSize = t("fileSize");
	const trustedBy = t("trustedBy");

	const carouselImages = CAROUSEL_IMAGES;
	const carouselImageHeight = CAROUSEL_IMAGE_HEIGHT;
	const carouselImageScales = CAROUSEL_IMAGE_SCALES;
	const visibleCount = 3;

	const nextSlide = useCallback(() => {
		if (carouselImages.length === 0) return;
		setCarouselIndex((prev) => (prev + 1) % carouselImages.length);
	}, [carouselImages.length]);

	useEffect(() => {
		if (carouselImages.length <= visibleCount) return;
		const timer = setInterval(nextSlide, 3000);
		return () => clearInterval(timer);
	}, [nextSlide, carouselImages.length, visibleCount]);

	const visibleImages =
		carouselImages.length > 0
			? Array.from(
					{ length: Math.min(visibleCount, carouselImages.length) },
					(_, i) => (carouselIndex + i) % carouselImages.length,
				)
			: [];

	return (
		<section className="relative min-h-[400px] w-full overflow-hidden sm:min-h-[450px] md:min-h-[500px]">
			<div className="absolute inset-0 bg-[linear-gradient(180deg,_#ffffff_0%,_#ffffff_75%,_#eef6e5_90%,_#ecfed4_100%)]" />

			<div className="container relative z-10 px-4 pb-10 pt-8 font-sans sm:px-6 sm:pb-14 sm:pt-12 md:pb-16 md:pt-14 lg:pb-20 lg:pt-16">
				<div className="flex h-full w-full flex-col gap-8 lg:flex-row lg:items-center lg:gap-12">
					<div className="max-w-[600px] pr-2 sm:pr-4 md:pr-6 lg:pr-8 xl:min-w-[600px]">
						<div className="-ml-[18px] inline-block">
							<div
								className="trustpilot-widget scale-125"
								data-locale="en-GB"
								data-template-id="5419b637fa0340045cd0c936"
								data-businessunit-id="68808142dd0a4c4d3e411b9c"
								data-style-height="24px"
								data-style-width="100%"
							>
								<a
									href="https://www.trustpilot.com/review/www.cncprotolabs.com?utm_medium=trustbox&utm_source=MicroTrustScore"
									target="_blank"
									rel="noopener"
								>
									Excellent Trustpilot
								</a>
							</div>
						</div>
						<h1 className="mb-4 text-xl font-bold leading-tight text-black sm:mb-6 sm:text-[24px] md:mb-8 md:text-[30px] lg:text-[36px] xl:text-[44px]">
							{title}
						</h1>

						<ul className="mb-8 space-y-2 sm:mb-10 sm:space-y-2.5 md:mb-12 md:space-y-3">
							{features.map((feature, index) => (
								<li key={index} className="flex items-start gap-2 sm:gap-3">
									<CheckIcon />
									<span className="text-xs leading-relaxed text-black sm:text-sm md:text-base">
										{feature}
									</span>
								</li>
							))}
						</ul>

						<div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
							<Link
								href="/upload"
								className="group inline-flex w-full items-center justify-center gap-2 rounded bg-themePrimary400 px-4 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-opacity-80 hover:text-white sm:w-fit sm:px-5 sm:py-3 md:px-6 md:py-3.5 md:text-base"
							>
								<CloudArrowUp className="h-4 w-4 shrink-0 md:h-[18px] md:w-[18px]" weight="fill" />
								{primaryButtonText}
							</Link>
							<button
								type="button"
								onClick={openModalInquiry}
								className="group inline-flex w-full items-center justify-center gap-2 rounded bg-mainColor px-4 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-opacity-80 sm:w-fit sm:px-5 sm:py-3 md:px-10 md:py-3.5 md:text-base"
							>
								{secondaryButtonText}
							</button>
						</div>
					</div>

					<div className="relative w-full flex-1 lg:max-w-[600px]">
						<div className="mx-auto max-w-[600px] bg-white p-6 shadow-[0_2px_12px_rgba(0,0,0,0.08)] md:p-8">
							{visibleImages.length > 0 && (
								<div className="mb-3 flex items-center justify-center gap-4">
									{visibleImages.map((imgIndex) => {
										const scale = carouselImageScales[imgIndex] ?? 1;
										return (
											<div
												key={`carousel-${imgIndex}`}
												className="relative w-[100px] overflow-hidden transition-opacity duration-500 md:w-[120px]"
												style={{ height: `${carouselImageHeight}px` }}
											>
												<Image
													src={carouselImages[imgIndex]}
													alt={`Part ${imgIndex + 1}`}
													fill
													className="object-contain"
													style={{ transform: scale !== 1 ? `scale(${scale})` : undefined }}
													sizes="120px"
												/>
											</div>
										);
									})}
								</div>
							)}

							<p className="mb-4 text-center text-sm text-[#39576f] md:text-lg">{uploadTitle}</p>

							<Link href="/upload" className="block">
								<div className="rounded-lg border-2 border-dashed border-mainColor bg-[#f8fff7] p-5 transition-all duration-300 hover:border-mainColor hover:bg-[#f8fff7] md:p-6">
									<p className="mb-3 text-center text-sm text-black md:text-lg">{uploadSubtitle}</p>
									<p className="mb-4 text-center text-[10px] text-gray-900 md:text-xs">{uploadFormats}</p>
									<div className="mb-3 flex justify-center">
										<UploadIcon className="h-12 w-12" />
									</div>
									{fileSize ? <p className="mb-2 text-center text-xs text-gray-900">{fileSize}</p> : null}
									<div className="flex items-center justify-center gap-1 text-xs text-gray-400">
										<LockSimple className="h-3 w-3" weight="fill" />
										<span>{uploadSecure}</span>
									</div>
								</div>
							</Link>

							{trustedBy ? (
								<p className="mt-5 text-center text-xs text-gray-900 md:text-sm">{trustedBy}</p>
							) : null}
						</div>
					</div>
				</div>
			</div>
		</section>
	);
};

export default Banner;
